import Helmet from "react-helmet";
import { connect, useDispatch } from "react-redux";
import {
  Separator,
} from "../../../components/StyledComponents";
import { CategoryDescription } from "./styles";
import NoSubscribed from "../components/NoSubscribed";
import { Page } from "../components/styles";
import Grid from "../../../components/Grid";
import { useEffect, useMemo, useState } from "react";
import { getGroupBySlug, resetGetGroup } from "../../../store/actions/group";
import { resetGetPost } from "../../../store/actions/post";
import Loader from "../../../components/LogoLoader";
import { Group, Subscription, User } from "../../../types";
import Content from "./components/Content";
import { Layout, useLayout } from "../../../providers/LayoutProvider";
import { isSubscribed } from "../../../helpers/isSubscribed";
import { Redirect, useLocation } from "react-router-dom";
import { isCongreso } from "../../../helpers/isCongreso";
import { isCommunity } from "../../../helpers/isCommunity";
import { isOwnGroup } from "../../../helpers/isOwnGroup";
import { CategoryDto } from "../../../types/category";
import { getCategory } from "../../../store/actions/category";
import { getUserPreferences } from "../../../store/actions/preferences";
import { getUserSubscriptions } from "../../../store/actions/user";
import { showPreference } from "../../../helpers/preferenceVisible";
import CategoryLockedBadge from "./components/CategoryLockedBadge";
import ModalUnlockedCategory from "../components/ModalUnlockedCategory";
import PostPreviewLayoutHeaderMenu from "./components/HeaderMenu";
import { useWindowSize } from "../../../hooks/useWindowSize";

interface LocationState {
  from: string;
}

interface ComponentProps {
  isLoggedIn: boolean;
  updatedCategory,
  updatedCategoryStates
  subscriptions: Subscription[];
  subscriptionsStates: {
    loading: boolean;
    error: string | boolean;
    success: boolean;
  };
  user: User;
  userStates: {
    loading: boolean;
    error: string | boolean;
    success: boolean;
  };
  group: Group;
  groupStates: {
    loading: boolean;
    error: string | boolean;
    success: boolean;
  };
  category: CategoryDto;
  categoryStates: {
    loading: boolean;
    error: string | boolean;
    success: boolean;
  };
  match;
  preferences;
  preferencesStates;
  reorderCategoryPostsStates
}

const Component = ({
  isLoggedIn,
  updatedCategory,
  updatedCategoryStates,
  subscriptions,
  subscriptionsStates,
  group,
  match,
  groupStates,
  user,
  userStates,
  category,
  categoryStates,
  preferences,
  preferencesStates,
  reorderCategoryPostsStates
}: ComponentProps) => {
  const { isMobile } = useWindowSize()
  const dispatch = useDispatch();
  const { setLayout, layout } = useLayout();
  const [goToSurvey, setGoTuSurvey] = useState<boolean>(false);
  const location = useLocation();

  const idCategory = match?.params?.category;

  const redirect = useMemo(() => {
    if (!!group) {
      return (
        groupStates?.error ||
        isCongreso(group?.id) ||
        isCommunity(group?.group_type) ||
        (!isOwnGroup({ group, expertId: user?.expert?.id }) &&
          group?.status === "draft")
      );
    }

    return false;
  }, [group, user]);

  const isLoading = useMemo(
    () =>
      subscriptionsStates.loading ||
      userStates.loading ||
      categoryStates.loading ||
      groupStates.loading ||
      preferencesStates.loading ||
      !group ||
      !category ||
      !user ||
      !preferences,
    [
      preferences,
      group,
      category,
      user,
      subscriptionsStates,
      userStates,
      categoryStates,
      groupStates,
      preferencesStates,
    ]
  );

  const layoutData = useMemo(
    (): Layout => ({
      ...layout,
      skin: "plain",
      footer: {
        visible: false,
      },
      header: {
        visible: true,
        ...(isOwnGroup({ group, expertId: user?.expert?.id }) && idCategory !== "without_category"
          ? { actions: [<PostPreviewLayoutHeaderMenu category={category} />] }
          : {}),
      },
      toolbar: {
        visible: isOwnGroup({ group, expertId: user?.expert?.id }),
      },
      body: {
        visible: true,
      },
    }),
    [layout, subscriptions, group, user, category, isLoading]
  );

  useEffect(() => {
    setLayout(layoutData);
  }, [subscriptions, group, user]);

  useEffect(() => {
    if (
      match?.params?.category &&
      (!category?.id || category?.id !== Number(match?.params?.category))
    ) {

      dispatch(
        getCategory({
          id: idCategory,
          group_id: match?.params?.group,
          enrich_unlockable_data_for_user: true,
        })
      );
    }
  }, [match]);

  useEffect(() => {
    if (category && (!group?.slug || group?.slug !== category?.group?.slug)) {
      dispatch(resetGetPost());
      match?.params &&
        !groupStates.loading &&
        dispatch(getGroupBySlug(category?.group?.slug));
    }
  }, [category]);

  useEffect(() => {
    dispatch(getUserPreferences());
    dispatch(getUserSubscriptions());

    return () => {
      dispatch(resetGetGroup());
    };
  }, []);

  useEffect(() => {
    if (group?.survey) {
      setGoTuSurvey(
        showPreference({
          preferences,
          preferencesStates,
          id: `survey-${group?.survey}`,
        })
      );
    }
  }, [preferences, preferencesStates, group]);

  const backURL = useMemo(() => {
    // Si viene del dashboard (location.state?.from existe), usar ese
    const previousPage = (location?.state as LocationState)?.from;
    if (previousPage) {
      return previousPage;
    }

    // Si no hay category o group aún, retornar undefined (se establecerá después)
    if (!category || !group) {
      return undefined;
    }

    // Si no viene del dashboard, verificar si soy el owner
    const isOwner = isOwnGroup({ group, expertId: user?.expert?.id });

    if (isOwner && category?.group) {
      // Construir la URL del dashboard según el tipo de grupo
      const groupType = category.group.group_type;
      if (groupType === "membership") {
        return `/dashboard/membership/${category.group.id}/posts/`;
      } else if (groupType === "experience") {
        return `/dashboard/experience/${category.group.id}/posts/`;
      } else if (groupType === "free_experience" || groupType === "community") {
        return `/dashboard/community/events/${category.group.id}/lead-magnets/`;
      }
    }

    // Si no soy owner, usar el valor por defecto
    return `/group/${category?.group?.slug}/library/`;
  }, [location?.state, group, user, category]);

  useEffect(() => {
    if (category && backURL) {
      setLayout({
        ...layout,
        page: {
          title: category?.name,
          back: backURL,
          showBack: true,
        },
      });
    }
  }, [category, backURL]);

  useEffect(() => {
    if (updatedCategoryStates.success) {
      const idCategory = match?.params?.category;
      const idGroup = match?.params?.group;

      dispatch(
        getCategory({
          id: idCategory,
          group_id: idGroup,
          enrich_unlockable_data_for_user: true,
        })
      );
    }
  }, [updatedCategoryStates.success])

  if (isLoading) {
    return <Loader />;
  }

  if (
    goToSurvey &&
    !isOwnGroup({ group, expertId: user?.expert?.id }) &&
    isSubscribed(subscriptions, group?.id)
  ) {
    return <Redirect to={`/group/${group.slug}/survey`} />;
  }

  if (redirect) {
    return <Redirect to="/404" />;
  }

  return (
    <>
      <Page>
        <Helmet>
          <title>{`${group?.group_name} - Categoría: ${category?.name}`}</title>
          <meta
            name="description"
            content={`${group?.group_name} - ${category?.name}`}
          />
        </Helmet>

        <Grid.Container style={{ position: "relative", zIndex: 1 }}>

          <div style={{ height: isMobile ? 13 : 36 }} />

          <Grid.Row>
            <Grid.Col lg={10} offset={{ lg: 1 }}>
              <CategoryLockedBadge style={{ marginBottom: isMobile ? 13 : 30 }} />
              <CategoryDescription>{category.description}</CategoryDescription>
            </Grid.Col>
          </Grid.Row>

          <div style={{ height: isMobile ? 24 : 36 }} />

          <Grid.Row>
            <Grid.Col lg={10} offset={{ lg: 1 }}>
              <Content category={category} />
              <Separator height={{ desktop: "32px", mobile: "16px" }} />
            </Grid.Col>
          </Grid.Row>
        </Grid.Container>
      </Page>

      <NoSubscribed />
      <ModalUnlockedCategory />
    </>
  );
};

const state = ({
  userStore,
  postStore,
  groupStore,
  categoryStore,
  preferencesStore,
}) => {
  const { data: category, states: categoryStates } =
    categoryStore.singleCategory;
  const { isLoggedIn } = userStore;
  const { data: subscriptions, states: subscriptionsStates } =
    userStore.subscriptions;
  const { data: user, states: userStates } = userStore.information;
  const { data: group, states: groupStates } = groupStore.group;
  const { data: preferences, states: preferencesStates } =
    preferencesStore.preferences;
  const { data: updatedCategory, states: updatedCategoryStates } = categoryStore.updateCategory;
  const { states: reorderCategoryPostsStates } = categoryStore.reorderCategoryPosts;

  return {
    subscriptions,
    subscriptionsStates,
    user,
    userStates,
    category,
    categoryStates,
    group,
    groupStates,
    isLoggedIn,
    preferences,
    preferencesStates,
    updatedCategory,
    updatedCategoryStates,
    reorderCategoryPostsStates
  };
};

export default connect(state)(Component);
